import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Minus,
  ShieldAlert,
  Unplug,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  DeliveryGate,
  GatesResponse,
  NotificationsOverview,
  ProviderCapability,
  ProviderHealth,
  ProviderRow,
  ReconciliationSummary,
  RoutingRow,
} from '@/client/types/notifications-infra';

export const DELIVERY_BASE = '/admin/communications/notifications';

export const DELIVERY_LINKS = [
  { to: `${DELIVERY_BASE}/delivery`, label: 'Overview', end: true },
  { to: `${DELIVERY_BASE}/routing`, label: 'Routing' },
  { to: `${DELIVERY_BASE}/reconciliation`, label: 'Reconciliation' },
  { to: `${DELIVERY_BASE}/providers`, label: 'Providers' },
] as const;

export const MESSAGING_TEMPLATES_HREF = '/admin/relationship/manage?tab=templates';
export const MESSAGING_TYPES_HREF = '/admin/relationship/manage?tab=types';

const GATE_COPY: Record<string, { title: string; description: string }> = {
  us_a2p_10dlc: {
    title: 'US A2P 10DLC',
    description:
      'Every US SMS is refused before dispatch. Carriers drop it while still billing.',
  },
  comms_sandbox: {
    title: 'Comms sandbox',
    description:
      'Suppresses delivery to non-allowlisted recipients on every channel.',
  },
};

export const PROBLEM_COPY: Record<string, { title: string; description: string }> = {
  no_template: {
    title: 'No template row',
    description: 'No notification_template row — workflow dies at template_lookup_failed.',
  },
  unmapped_email: {
    title: 'Unmapped email',
    description:
      'Email is allowed, but no provider template id is set — prepare_notification drops the channel while settings still show the toggle.',
  },
  sms_no_text: {
    title: 'SMS has no text',
    description: 'SMS is allowed, but sms_text is empty so nothing can be rendered.',
  },
};

export const DeliveryAccessDenied = ({ code = 'messaging.view' }: { code?: string }) => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      This page requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">{code}</code> permission.
    </p>
  </div>
);

export const DeliverySubnav = () => (
  <div className="flex flex-wrap gap-2">
    {DELIVERY_LINKS.map((link) => (
      <NavLink
        key={link.to}
        to={link.to}
        end={'end' in link ? link.end : false}
        className={({ isActive }) =>
          `rounded-md border px-3 py-1.5 text-sm transition-colors duration-150 ${
            isActive
              ? 'border-blue-200 bg-blue-50 text-blue-800'
              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
          }`
        }
      >
        {link.label}
      </NavLink>
    ))}
  </div>
);

export const DeliveryPageShell = ({
  icon,
  title,
  description,
  children,
  loading,
  actions,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
  loading?: boolean;
  actions?: ReactNode;
}) => {
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-56" />
          <Skeleton className="h-10 w-80" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-1 flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-white">
                {icon}
              </div>
              <h1 className="text-2xl font-black text-gray-900">{title}</h1>
            </div>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
          {actions}
        </div>
        <DeliverySubnav />
        {children}
      </div>
    </div>
  );
};

export function providerKey(row: ProviderRow | ProviderHealth | string | undefined | null): string {
  if (!row) return '';
  if (typeof row === 'string') return row;
  return String(row.provider ?? row.id ?? ('name' in row ? row.name : '') ?? '').trim();
}

export function hasCapability(row: ProviderRow | undefined, cap: ProviderCapability): boolean {
  return (row?.capabilities ?? []).includes(cap);
}

export function routedCountryCount(row: ProviderRow): number | null {
  const raw = row.routed_countries;
  if (raw == null) return null;
  if (Array.isArray(raw)) return raw.length;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function formatProviderName(id: string): string {
  const known: Record<string, string> = {
    brevo: 'Brevo',
    ses: 'Amazon SES',
    sns: 'Amazon SNS',
    hubtel: 'Hubtel',
    telnyx: 'Telnyx',
    twilio: 'Twilio',
    mailgun: 'Mailgun',
    in_app: 'In-app',
  };
  if (known[id]) return known[id];
  return id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatChannel(channel: string): string {
  const known: Record<string, string> = {
    email: 'Email',
    sms: 'SMS',
    in_app: 'In-app',
    push: 'Push',
  };
  return known[channel] ?? channel.replace(/_/g, ' ');
}

export function formatCapability(cap: string): string {
  const known: Record<string, string> = {
    templates: 'Templates',
    events: 'Events',
    balance: 'Balance',
    senders: 'Senders',
    test_send: 'Test send',
  };
  return known[cap] ?? cap.replace(/_/g, ' ');
}

export function gateId(gate: DeliveryGate): string {
  return String(gate.id ?? gate.key ?? gate.name ?? '').trim();
}

export function isGateActive(gate: DeliveryGate): boolean {
  if (gate.active === false || gate.active === 0) return false;
  if (gate.active === true || gate.active === 1) return true;
  return true;
}

export function normalizeGates(raw: GatesResponse | NotificationsOverview | DeliveryGate[] | undefined | null): DeliveryGate[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map((g) =>
      typeof g === 'string'
        ? { id: g, key: g, active: true }
        : g,
    );
  }

  const obj = raw as GatesResponse & NotificationsOverview & {
    active_kill_switches?: unknown;
  };
  const list =
    obj.gates ??
    obj.kill_switches ??
    obj.active_gates ??
    obj.active_kill_switches;

  if (Array.isArray(list)) {
    return list.map((g) =>
      typeof g === 'string' ? { id: g, key: g, active: true } : (g as DeliveryGate),
    );
  }

  if (list && typeof list === 'object') {
    return Object.entries(list as Record<string, unknown>).map(([key, value]) => ({
      id: key,
      key,
      active: value === true || value === 1 || value === '1' || value === 'true',
    }));
  }

  if (obj.active && typeof obj.active === 'object' && !Array.isArray(obj.active)) {
    return Object.entries(obj.active as Record<string, unknown>).map(([key, value]) => ({
      id: key,
      key,
      active: value === true || value === 1 || value === '1' || value === 'true',
    }));
  }

  if (Array.isArray(obj.active)) {
    return (obj.active as string[]).map((id) => ({ id, key: id, active: true }));
  }

  return [];
}

export function pickMappingHealth(
  overview: NotificationsOverview | undefined,
): ReconciliationSummary | undefined {
  if (!overview) return undefined;
  return overview.mapping_health ?? overview.reconciliation ?? overview.summary;
}

export function routingRows(raw: { routes?: RoutingRow[]; routing?: RoutingRow[] } | undefined): RoutingRow[] {
  if (!raw) return [];
  return raw.routes ?? raw.routing ?? [];
}

export function routingCountry(row: RoutingRow): string {
  return String(row.cc ?? row.iso ?? row.country ?? '').toUpperCase();
}

export function gateTitle(gate: DeliveryGate): string {
  const id = gateId(gate);
  return GATE_COPY[id]?.title ?? gate.name ?? id.replace(/_/g, ' ');
}

export function gateDescription(gate: DeliveryGate): string {
  const id = gateId(gate);
  return (
    gate.description ||
    gate.note ||
    GATE_COPY[id]?.description ||
    'Silently suppresses delivery — no user-visible error.'
  );
}

export const GatesBanner = ({
  gates,
  loading,
}: {
  gates: DeliveryGate[];
  loading?: boolean;
}) => {
  if (loading) {
    return <Skeleton className="h-20 w-full rounded-xl" />;
  }

  const active = gates.filter(isGateActive);
  const inactive = gates.filter((g) => !isGateActive(g));

  if (gates.length === 0) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm text-gray-500 shadow-sm">
        No delivery gates reported.
      </div>
    );
  }

  if (active.length === 0) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <div>
          <p className="text-sm font-semibold text-emerald-900">No kill-switches active</p>
          <p className="text-xs text-emerald-800/80">
            Delivery is not being silently suppressed by a gate.
            {inactive.length > 0
              ? ` Inactive: ${inactive.map((g) => gateTitle(g)).join(', ')}.`
              : ''}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 shadow-sm">
      <div className="mb-2 flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <div>
          <p className="text-sm font-semibold text-amber-950">
            Kill-switch{active.length === 1 ? '' : 'es'} active — delivery is suppressed silently
          </p>
          <p className="text-xs text-amber-900/80">
            Check these first when someone reports that no messages are arriving. Neither produces
            a user-visible error.
          </p>
        </div>
      </div>
      <ul className="space-y-2 pl-6">
        {active.map((gate) => (
          <li key={gateId(gate) || gateTitle(gate)}>
            <p className="text-sm font-medium text-amber-950">{gateTitle(gate)}</p>
            <p className="text-xs text-amber-900/80">{gateDescription(gate)}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};

export type HealthRender =
  | { kind: 'loading' }
  | { kind: 'proxy_unavailable' }
  | { kind: 'not_probeable' }
  | { kind: 'healthy'; balance?: string }
  | { kind: 'unhealthy'; detail?: string };

export function resolveHealth(
  row: ProviderRow | undefined,
  health: ProviderHealth | undefined,
  proxyUnavailable: boolean,
  healthLoading: boolean,
): HealthRender {
  if (healthLoading) return { kind: 'loading' };

  const probeable = health?.probeable ?? row?.probeable;
  if (probeable === false) return { kind: 'not_probeable' };

  if (proxyUnavailable) return { kind: 'proxy_unavailable' };

  if (!health) {
    if (probeable === true) return { kind: 'proxy_unavailable' };
    return { kind: 'not_probeable' };
  }

  if (health.ok === true) {
    const bal =
      health.balance == null || health.balance === ''
        ? undefined
        : `${health.balance}${health.currency ? ` ${health.currency}` : ''}`;
    return { kind: 'healthy', balance: bal };
  }

  return {
    kind: 'unhealthy',
    detail: health.detail || health.error || undefined,
  };
}

export const HealthStatus = ({ state }: { state: HealthRender }) => {
  if (state.kind === 'loading') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking…
      </span>
    );
  }
  if (state.kind === 'not_probeable') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-400" title="No balance API">
        <Minus className="h-3 w-3" />
        —
      </span>
    );
  }
  if (state.kind === 'proxy_unavailable') {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs text-slate-600"
        title="ff-api-external could not be reached — not a vendor fault"
      >
        <Unplug className="h-3 w-3" />
        Unavailable
      </span>
    );
  }
  if (state.kind === 'healthy') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
        <CheckCircle2 className="h-3 w-3" />
        {state.balance ?? 'Healthy'}
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium text-red-700"
      title={state.detail || 'Vendor answered unhealthily'}
    >
      <AlertTriangle className="h-3 w-3" />
      Unhealthy
      {state.detail ? <span className="font-normal text-red-600/80">· {state.detail}</span> : null}
    </span>
  );
};

export function getApiStatus(error: unknown): number | undefined {
  const err = error as { status?: number; statusCode?: number };
  return err?.status ?? err?.statusCode;
}

export function getApiErrorCode(error: unknown): string | undefined {
  const err = error as {
    data?: { error?: string; data?: { error?: string } };
    response?: { data?: { error?: string; data?: { error?: string } } };
  };
  const code =
    err?.data?.data?.error ??
    err?.data?.error ??
    err?.response?.data?.data?.error ??
    err?.response?.data?.error;
  return typeof code === 'string' ? code : undefined;
}

export function getApiErrorDetail(error: unknown): string | undefined {
  const err = error as {
    data?: { detail?: string; data?: { detail?: string } };
    response?: { data?: { detail?: string; data?: { detail?: string } } };
  };
  const detail =
    err?.data?.data?.detail ??
    err?.data?.detail ??
    err?.response?.data?.data?.detail ??
    err?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : undefined;
}

export function getMappedBy(error: unknown): Array<{ ntmplid?: number; name_system?: string }> {
  const err = error as {
    data?: { mapped_by?: unknown; data?: { mapped_by?: unknown } };
    response?: { data?: { mapped_by?: unknown; data?: { mapped_by?: unknown } } };
  };
  const raw =
    err?.data?.data?.mapped_by ??
    err?.data?.mapped_by ??
    err?.response?.data?.data?.mapped_by ??
    err?.response?.data?.mapped_by;
  return Array.isArray(raw) ? raw : [];
}

export function describeProviderError(error: unknown): {
  title: string;
  description: string;
  code?: string;
  status?: number;
} {
  const status = getApiStatus(error);
  const code = getApiErrorCode(error);
  const detail = getApiErrorDetail(error);
  const message = getApiErrorMessage(error, 'Request failed');

  if (status === 409 || code === 'template_in_use') {
    const mapped = getMappedBy(error);
    const names = mapped
      .map((m) => m.name_system || (m.ntmplid != null ? `#${m.ntmplid}` : ''))
      .filter(Boolean)
      .join(', ');
    return {
      title: 'Template is still mapped',
      description: names
        ? `Unmap via messaging templates first: ${names}.`
        : 'Unmap this template from notification_template rows before deleting.',
      code,
      status,
    };
  }

  if (status === 502 || code === 'provider_error') {
    return {
      title: 'Vendor rejected the request',
      description: detail || message,
      code,
      status,
    };
  }

  if (status === 503 || code === 'proxy_unavailable' || code === 'probe_unavailable') {
    return {
      title: 'Delivery proxy unavailable',
      description:
        'ff-api-external is unreachable. This is not a vendor fault — check the proxy, not Brevo/Twilio.',
      code,
      status,
    };
  }

  if (status === 400 || code === 'missing_fields' || code === 'no_valid_fields') {
    return {
      title: 'Invalid payload',
      description: detail || message,
      code,
      status,
    };
  }

  return { title: 'Request failed', description: detail || message, code, status };
}

export const CapabilityChips = ({ caps }: { caps: ProviderCapability[] }) => {
  if (!caps.length) {
    return <span className="text-xs text-gray-400">No capabilities</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {caps.map((cap) => (
        <span
          key={cap}
          className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[11px] font-medium text-gray-700"
        >
          {formatCapability(cap)}
        </span>
      ))}
    </div>
  );
};

export const SourceBadge = ({ source }: { source?: string | null }) => {
  if (!source) return <span className="text-xs text-gray-400">—</span>;
  const env = source.startsWith('env:');
  return (
    <code
      className={`rounded px-1.5 py-0.5 text-[11px] ${
        env ? 'bg-violet-50 text-violet-800' : 'bg-slate-100 text-slate-700'
      }`}
      title="Why this country uses this provider"
    >
      {source}
    </code>
  );
};
